import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectUser } from 'redux/slices/userSlice';
import { selectCompany } from 'redux/slices/Company/CompanySlice';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination
} from '@mui/material';
import { getSearchCompanyLogs } from 'services/rest/Company/GetCompanyData';
import DropdownSelect from 'components/inputs/DropdownSelect/DropdownSelect';
import { FilterCampaigns } from '../Styled';
import Input from 'components/inputs/Input';

export interface LogsTableFormProps {
  selectedCompany: any;
}

const LogsTable: React.FC<LogsTableFormProps> = props => {
  const { selectedCompany } = props;

  const { t } = useTranslation(['logs']);
  const { currentUser } = useSelector(selectUser);
  const { company } = useSelector(selectCompany);
  const [logs, setLogs] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const timezone = company?.timezone || 'UTC';

  const roleOptions = [
    { label: t('logs:all_roles'), value: 'ALL' },
    { label: 'Administrator', value: 'Administrator' },
    { label: 'Ambassador', value: 'Ambassador' }
  ];

  const [filterText, setFilterText] = useState('');
  const [selectedRole, setSelectedRole] = useState(roleOptions[0]);

  const fetchLogs = async () => {
    const data = await getSearchCompanyLogs(
      currentUser.token,
      1,
      500,
      selectedCompany
    );
    if (data?.rows) setLogs(data.rows);
  };

  useEffect(() => {
    selectedCompany ? fetchLogs() : '';
  }, [selectedCompany, company]);

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone
    }).format(date);
  };

  const selectRoleOption = (event: any) => {
    setSelectedRole(event);
    setPage(0);
  };

  const logsData = logs || [];

  const filteredLogs = logsData
    .filter(
      log =>
        log.accountName?.toLowerCase().includes(filterText.toLowerCase()) ||
        log.id?.toString().includes(filterText) ||
        log.accountEmail?.toLowerCase().includes(filterText.toLowerCase()) ||
        log.role?.toLowerCase().includes(filterText.toLowerCase()) ||
        log.action?.toLowerCase().includes(filterText.toLowerCase())
    )
    .filter(
      log => selectedRole.value === 'ALL' || log.role === selectedRole.value
    );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <FilterCampaigns>
        <Input
          type="search"
          placeholder={t('logs:filter_logs')}
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
        <span>{t('logs:role')}</span>
        <DropdownSelect
          options={roleOptions}
          value={selectedRole}
          onSelect={selectRoleOption}
        />
      </FilterCampaigns>
      <TableContainer component={Paper} className="table_container">
        <Table
          sx={{ minWidth: 750 }}
          size="medium"
          aria-label="logs table"
          className="table_center"
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">{t('logs:id_log')}</TableCell>
              <TableCell align="center">{t('logs:role')}</TableCell>
              <TableCell align="center">{t('logs:account_name')}</TableCell>
              <TableCell align="center">{t('logs:account_email')}</TableCell>
              <TableCell align="center">{t('logs:action')}</TableCell>
              <TableCell align="center">{t('logs:created_at')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="table_body">
            {(rowsPerPage > 0
              ? filteredLogs.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
              : filteredLogs
            ).map((log, index) => (
              <TableRow
                key={log.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell align="center">{log.id}</TableCell>
                <TableCell align="center">{log.role}</TableCell>
                <TableCell align="center">{log.accountName}</TableCell>
                <TableCell align="center">{log.accountEmail}</TableCell>
                <TableCell align="center">{log.action}</TableCell>
                <TableCell align="center">
                  {formatDate(log.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={logs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('logs:rows_per_page')}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </TableContainer>
    </>
  );
};

export default LogsTable;
